import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export const clientStatusOptions = [
  "New Lead",
  "Discovery Scheduled",
  "Discovery Completed",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Awaiting Approval",
  "Agreement Signed",
  "Invoice Created",
  "Invoice Sent",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Deposit Paid",
  "Paid In Full",
  "Payment Received",
  "Payment Failed",
  "Refunded",
  "Welcome Sent",
  "Onboarding Sent",
  "Onboarding Complete",
  "Build In Progress",
  "Awaiting Client Content",
  "On Hold",
  "In Production",
  "Internal Review",
  "Awaiting Client Review",
  "Client Review",
  "Follow-Up Sent",
  "Testimonial Requested",
  "Referral Invitation Sent",
  "Final Delivery",
  "Completed",
] as const;

export const clientTypeOptions = [
  "Individual",
  "Family",
  "Business",
  "Nonprofit",
  "Church",
  "School",
  "Community Organization",
  "Partner Referral",
] as const;

export const serviceOptions = [
  "RSVP Hub",
  "Memory Hub",
  "Archive Hub",
  "Memory Media Vault",
  "Signature Song",
  "Signature Highlight Reel",
] as const;

export const noteCategoryOptions = ["Call", "Email", "Text", "Meeting", "Payment", "Issue", "Client Request", "Internal Note"] as const;

export const documentTypeOptions = [
  "Discovery Call Notes",
  "Proposal",
  "Signed Service Agreement",
  "Invoice",
  "Payment Record",
  "Welcome Email",
  "Kickoff Notes",
  "Client Intake Form",
  "RSVP Hub Form",
  "Memory Hub Form",
  "Archive Hub Form",
  "Memory Media Vault Form",
  "Signature Song Form",
  "Signature Highlight Reel Form",
  "Project Files",
  "Final Delivery Notes",
  "Other",
] as const;

export const paymentStatusOptions = [
  "Not Sent",
  "Payment Link Sent",
  "Deposit Due",
  "Deposit Paid",
  "Balance Due",
  "Invoice Created",
  "Invoice Sent",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Paid In Full",
  "Payment Failed",
  "Refunded",
  "Canceled",
] as const;

export const checklistStatusOptions = ["Not Started", "In Progress", "Completed", "Blocked"] as const;
export const followUpPriorityOptions = ["Low", "Medium", "High", "Urgent"] as const;
export const followUpStatusOptions = ["Pending", "Completed", "Canceled"] as const;
export const healthOptions = ["On Track", "Needs Attention", "Overdue"] as const;

export type ClientStatus = (typeof clientStatusOptions)[number];
export type ClientType = (typeof clientTypeOptions)[number];
export type NoteCategory = (typeof noteCategoryOptions)[number];
export type DocumentType = (typeof documentTypeOptions)[number];
export type PaymentStatus = (typeof paymentStatusOptions)[number];
export type ChecklistStatus = (typeof checklistStatusOptions)[number];
export type FollowUpPriority = (typeof followUpPriorityOptions)[number];
export type FollowUpStatus = (typeof followUpStatusOptions)[number];
export type ClientHealth = (typeof healthOptions)[number];

export interface ClientQuickLink {
  id: string;
  label: string;
  url: string;
  notes: string;
}

export interface ClientNote {
  id: string;
  date: string;
  time: string;
  category: NoteCategory;
  text: string;
  followUpDate: string;
}

export interface ClientDocument {
  id: string;
  type: DocumentType;
  label: string;
  url: string;
  notes: string;
}

export interface PaymentSummary {
  totalProjectValue: string;
  depositAmount: string;
  paymentsReceived: string;
  paymentStatus: PaymentStatus;
  lastPaymentDate: string;
  paymentNotes: string;
}

export interface ProjectChecklistItem {
  id: string;
  label: string;
  status: ChecklistStatus;
  completionDate: string;
  notes: string;
}

export interface ClientFollowUp {
  id: string;
  followUpDate: string;
  reason: string;
  priority: FollowUpPriority;
  status: FollowUpStatus;
  notes: string;
}

export interface KtdClientRecord {
  id: string;
  clientName: string;
  email: string;
  phone: string;
  organizationName: string;
  clientType: ClientType;
  eventName: string;
  eventDate: string;
  selectedServices: string[];
  selectedPackage: string;
  projectStatus: ClientStatus;
  notes: string;
  healthOverride: "" | ClientHealth;
  quickLinks: ClientQuickLink[];
  timelineNotes: ClientNote[];
  documents: ClientDocument[];
  paymentSummary: PaymentSummary;
  checklist: ProjectChecklistItem[];
  followUps: ClientFollowUp[];
  createdAt: string;
  updatedAt: string;
}

export type NewClientInput = Pick<
  KtdClientRecord,
  | "clientName"
  | "email"
  | "phone"
  | "organizationName"
  | "clientType"
  | "eventName"
  | "eventDate"
  | "selectedServices"
  | "selectedPackage"
  | "projectStatus"
  | "notes"
>;

const clientManagementStore = createBusinessCenterOperationalStateStore<KtdClientRecord[]>({
  moduleKey: "business_center_client_management",
  scope: { scopeKey: "client-management", scopeType: "platform" },
  seed: createSeedClients,
  normalize: (clients) => clients.map(normalizeClientRecord),
  isEmpty: (clients) => !Array.isArray(clients) || clients.length === 0,
});

const defaultChecklistLabels = [
  "Website Inquiry Received",
  "Discovery Call Scheduled",
  "Discovery Call Completed",
  "Proposal Created",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Proposal Accepted",
  "Agreement Sent",
  "Agreement Signed",
  "Invoice Sent",
  "Deposit Paid",
  "Invoice Created",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Paid In Full",
  "Welcome Email Sent",
  "Onboarding Sent",
  "Onboarding Complete",
  "Kickoff Call Scheduled",
  "Kickoff Call Completed",
  "Client Forms Submitted",
  "Project Build Started",
  "Build In Progress",
  "Internal Review",
  "Project Build Completed",
  "Awaiting Client Review",
  "Client Review",
  "Revisions Requested",
  "Revisions Completed",
  "Final Approval",
  "Final Delivery",
  "Follow-Up Sent",
  "Testimonial Requested",
  "Referral Invitation Sent",
  "Project Completed",
];

export function createDefaultChecklist(): ProjectChecklistItem[] {
  return defaultChecklistLabels.map((label) => ({
    id: crypto.randomUUID(),
    label,
    status: "Not Started",
    completionDate: "",
    notes: "",
  }));
}

export function defaultPaymentSummary(): PaymentSummary {
  return {
    totalProjectValue: "",
    depositAmount: "",
    paymentsReceived: "",
    paymentStatus: "Not Sent",
    lastPaymentDate: "",
    paymentNotes: "",
  };
}

function createSeedClients(): KtdClientRecord[] {
  return [];
}

export function createClientRecord(input: NewClientInput): KtdClientRecord {
  const now = new Date().toISOString();
  return {
    ...input,
    id: crypto.randomUUID(),
    healthOverride: "",
    quickLinks: [],
    timelineNotes: [],
    documents: [],
    paymentSummary: defaultPaymentSummary(),
    checklist: createDefaultChecklist(),
    followUps: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function loadClients(): KtdClientRecord[] {
  return clientManagementStore.read();
}

export async function refreshClients() {
  return clientManagementStore.refresh();
}

export function saveClients(clients: KtdClientRecord[]) {
  clientManagementStore.write(clients);
}

export function upsertClient(clients: KtdClientRecord[], nextClient: KtdClientRecord) {
  const now = new Date().toISOString();
  const normalized = normalizeClientRecord({ ...nextClient, updatedAt: now });
  const exists = clients.some((client) => client.id === normalized.id);
  const nextClients = exists
    ? clients.map((client) => (client.id === normalized.id ? normalized : client))
    : [normalized, ...clients];

  saveClients(nextClients);
  return nextClients;
}

export function formatDisplayDate(value: string) {
  if (!value) return "Not set";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
}

export function formatDateTime(value: string) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function calculateBalance(summary: PaymentSummary) {
  const total = Number(summary.totalProjectValue || 0);
  const received = Number(summary.paymentsReceived || 0);
  const balance = total - received;
  return Number.isFinite(balance) ? Math.max(balance, 0) : 0;
}

export function currency(value: string | number) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "$0";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount);
}

export function isDateOverdue(value: string) {
  if (!value) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${value}T00:00:00`);
  return !Number.isNaN(target.getTime()) && target < today;
}

export function getClientHealth(client: KtdClientRecord): ClientHealth {
  if (client.healthOverride) return client.healthOverride;

  const hasOverdueFollowUp = client.followUps.some(
    (followUp) => followUp.status === "Pending" && isDateOverdue(followUp.followUpDate),
  );
  const hasBlockedChecklist = client.checklist.some((item) => item.status === "Blocked");
  if (hasOverdueFollowUp || hasBlockedChecklist) return "Overdue";

  const hasPendingPayment = [
    "Payment Link Sent",
    "Deposit Due",
    "Balance Due",
    "Invoice Created",
    "Invoice Sent",
    "Partial Payment Received",
    "Awaiting Final Payment",
  ].includes(client.paymentSummary.paymentStatus);
  const clientForms = client.checklist.find((item) => item.label === "Client Forms Submitted");
  const formsMissingLate =
    clientForms?.status !== "Completed" &&
    ["In Production", "Awaiting Client Content", "On Hold", "Client Review", "Final Delivery", "Completed"].includes(client.projectStatus);
  if (hasPendingPayment || formsMissingLate) return "Needs Attention";

  return "On Track";
}

export function sortNotesNewestFirst(notes: ClientNote[]) {
  return [...notes].sort((a, b) => `${b.date}T${b.time || "00:00"}`.localeCompare(`${a.date}T${a.time || "00:00"}`));
}

export function sortFollowUps(followUps: ClientFollowUp[]) {
  return [...followUps].sort((a, b) => a.followUpDate.localeCompare(b.followUpDate));
}

function normalizeClientRecord(record: KtdClientRecord): KtdClientRecord {
  return {
    ...record,
    selectedServices: Array.isArray(record.selectedServices) ? record.selectedServices : [],
    healthOverride: record.healthOverride || "",
    quickLinks: Array.isArray(record.quickLinks) ? record.quickLinks : [],
    timelineNotes: Array.isArray(record.timelineNotes) ? record.timelineNotes : [],
    documents: Array.isArray(record.documents) ? record.documents : [],
    paymentSummary: record.paymentSummary || defaultPaymentSummary(),
    checklist: Array.isArray(record.checklist) && record.checklist.length ? record.checklist : createDefaultChecklist(),
    followUps: Array.isArray(record.followUps) ? record.followUps : [],
  };
}
